import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { useState } from "react";
import { Plus, ToggleRight, ToggleLeft, Package } from "lucide-react";
import { toast } from "sonner";
import { formatEUR, errText } from "@/lib/api";
import { IntegrationSettings, DEFAULT_ENDPOINTS } from "@/components/IntegrationSettings";

const cleanEndpoints = (eps) => (eps || []).filter((e) => e.name.trim() && e.path.trim()).map((e) => ({ ...e, name: e.name.trim(), path: e.path.trim().startsWith("/") ? e.path.trim() : `/${e.path.trim()}` }));
const AUTH_EMPTY = { auth_method: "", auth_header_name: "", basic_username: "" };
const empty = { name: "", slug: "", description: "", price: 0, active: true, modules: [{ name: "", description: "", price: 0 }], api_base_url: "", app_api_key: "", ...AUTH_EMPTY, endpoints: DEFAULT_ENDPOINTS };

export default function AdminProducts() {
  const api = useApi();
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => (await api.get("/admin/products")).data,
  });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const create = useMutation({
    mutationFn: async () => {
      const { app_api_key_hint, ...rest } = form;
      const body = { ...rest, price: Number(form.price) || 0, modules: form.modules.filter((m) => m.name).map((m) => ({ ...m, price: Number(m.price) || 0 })), endpoints: cleanEndpoints(form.endpoints) };
      return (form.id ? await api.put(`/admin/products/${form.id}`, body) : await api.post("/admin/products", body)).data;
    },
    onSuccess: () => { const wasEdit = !!form.id; setOpen(false); setForm(empty); qc.invalidateQueries({ queryKey: ["admin-products"] }); toast.success(wasEdit ? "Prodotto aggiornato" : "Prodotto pubblicato — email di richiesta inviata al Super Admin"); },
    onError: (e) => toast.error(errText(e)),
  });

  const toggle = useMutation({
    mutationFn: async (id) => (await api.patch(`/admin/products/${id}/toggle`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-products"] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Prodotti</h1>
          <p className="text-sm text-slate-500 mt-1">Catalogo rivendibile. Ogni prodotto ha moduli e un'API key per l'integrazione bidirezionale.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setForm(empty); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="btn-new-product"><Plus size={14} className="mr-1" /> Nuovo prodotto</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{form.id ? "Modifica prodotto" : "Nuovo prodotto"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" /></div>
              <div><Label>Slug</Label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="booking-hub" data-testid="prod-slug" /></div>
              <div><Label>Descrizione</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-desc" /></div>
              <div><Label>Prezzo licenza annua (€)</Label><Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" /><p className="text-xs text-slate-500 mt-1">Base di calcolo per le provvigioni a percentuale.</p></div>
              <IntegrationSettings api={api} form={form} setForm={setForm} open={open} />
              <div>
                <Label>Moduli disponibili <span className="text-xs font-normal text-slate-500">(nome · descrizione · prezzo annuo € aggiunto al canone base)</span></Label>
                {form.modules.map((m, i) => (
                  <div key={i} className="grid grid-cols-6 gap-2 mt-2">
                    <Input className="col-span-2" value={m.name} onChange={(e) => { const mods = [...form.modules]; mods[i] = { ...mods[i], name: e.target.value }; setForm({ ...form, modules: mods }); }} placeholder="Nome modulo" data-testid={`prod-mod-name-${i}`} />
                    <Input className="col-span-3" value={m.description} onChange={(e) => { const mods = [...form.modules]; mods[i] = { ...mods[i], description: e.target.value }; setForm({ ...form, modules: mods }); }} placeholder="Descrizione" />
                    <Input type="number" min="0" step="0.01" value={m.price ?? 0} onChange={(e) => { const mods = [...form.modules]; mods[i] = { ...mods[i], price: e.target.value }; setForm({ ...form, modules: mods }); }} placeholder="€" title="Prezzo annuo modulo (€)" data-testid={`prod-mod-price-${i}`} />
                  </div>
                ))}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setForm({ ...form, modules: [...form.modules, { name: "", description: "", price: 0 }] })}>+ Aggiungi modulo</Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => create.mutate()} className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="btn-save-product">{form.id ? "Salva" : "Pubblica"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading && <div className="text-slate-500">Caricamento…</div>}
        {data.map((p) => (
          <div key={p.id} className="bg-white border border-[#E2DDD5] rounded-xl p-5" data-testid={`product-card-${p.slug}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2"><Package size={16} className="text-[#B84A2B]" /><div className="font-semibold">{p.name}</div></div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{p.slug}</div>
              </div>
              <button onClick={() => toggle.mutate(p.id)} className="text-slate-500 hover:text-[#B84A2B]" data-testid={`toggle-${p.slug}`}>
                {p.active ? <ToggleRight size={22} className="text-emerald-600" /> : <ToggleLeft size={22} />}
              </button>
            </div>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{p.description}</p>
            <div className="mt-3 flex items-center justify-between">
              <div className="text-lg font-semibold" data-testid={`product-price-${p.slug}`}>{formatEUR(p.price || 0)}<span className="text-xs font-normal text-slate-500"> / anno</span></div>
              <Button variant="outline" size="sm" data-testid={`edit-product-${p.slug}`} onClick={() => { setForm({ id: p.id, name: p.name, slug: p.slug, description: p.description || "", price: p.price || 0, active: p.active, modules: p.modules?.length ? p.modules : [{ name: "", description: "", price: 0 }], api_base_url: p.api_base_url || "", app_api_key: "", app_api_key_hint: p.app_api_key_hint || "", auth_method: p.auth_method || "", auth_header_name: p.auth_header_name || "", basic_username: p.basic_username || "", endpoints: p.endpoints?.length ? p.endpoints : DEFAULT_ENDPOINTS }); setOpen(true); }}>Modifica</Button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {p.modules?.map((m) => (<span key={m.id} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F2F0EB] border border-[#E2DDD5]" data-testid={`module-chip-${m.id}`}>{m.name}{m.price ? ` · +${formatEUR(m.price)}` : ""}</span>))}
            </div>
            {p.api_base_url && <div className="mt-3 text-[11px] font-mono text-slate-500 break-all">{p.api_base_url}</div>}
            <div className="mt-1 text-[11px]" data-testid={`product-app-status-${p.slug}`}>{p.api_base_url && p.app_api_key_set ? <span className="text-emerald-700">App collegata · chiave {p.app_api_key_hint}</span> : <span className="text-amber-700">{p.api_base_url ? "API key dell'app mancante" : "App esterna non collegata"}</span>}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
